import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  FileText,
  Loader2,
  Search,
  Pencil,
  Trash2,
  ExternalLink,
  AlertCircle,
  ArrowUpDown,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription, 
  DialogFooter,
} from "../../components/ui/dialog";
import usePages from "../../hooks/usePages";
import { useAuth } from "../../context/AuthContext";

const PageList = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("pages.create");
  const canUpdate = hasPermission("pages.update");
  const canDelete = hasPermission("pages.delete");
  const canReorder = hasPermission("pages.reorder");
  const { pages, loading, deletePage: apiDeletePage, search, setSearch } = usePages();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const getPublicPagePath = (page) => {
    if (page.category === "Capabilities") return `/capabilities/${page.slug}`;
    if (page.category === "Industries") return `/industries/${page.slug}`;
    return `/${page.slug}`;
  };

  const getCategoryBadgeClasses = (category) => {
    if (category === "Capabilities") return "text-blue-600 bg-blue-100";
    if (category === "Industries") return "text-purple-600 bg-purple-100";
    return "text-emerald-700 bg-emerald-100";
  };

  const handleDelete = async () => {
    if (itemToDelete === null || itemToDelete === undefined) return;

    try {
      setIsDeleting(true);
      const success = await apiDeletePage(itemToDelete);
      if (success) {
        setDeleteDialogOpen(false);
        setItemToDelete(null);
      }
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
            <span className="p-2 bg-primary/10 text-primary rounded-2xl">
              <FileText size={28} />
            </span>
            Pages
          </h1>
          <p className="text-slate-500 mt-1 font-medium">
            Manage your custom pages, categories, and slugs.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {canReorder && <Button
            onClick={() => navigate("/pages/reorder")}
            variant="outline"
            className="border-primary text-primary hover:bg-primary/5 rounded-2xl px-6 py-6 shadow-md transition-all hover:-translate-y-0.5"
          >
            <ArrowUpDown size={20} className="mr-2" />
            Reorder Pages
          </Button>}
          {canCreate && <Button
            onClick={() => navigate("/pages/add")}
            className="bg-primary hover:bg-primary/90 text-secondary rounded-2xl px-6 py-6 shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5"
          >
            <Plus size={20} className="mr-2" />
            New Page
          </Button>}
        </div>
      </div>

      <div className="flex items-center gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
            size={18}
          />
          <Input
            placeholder="Search by name, slug or category..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 rounded-2xl border-slate-200 focus-visible:ring-primary"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white/50 backdrop-blur-sm rounded-3xl border border-dashed border-slate-300">
          <Loader2 className="animate-spin text-primary mb-4" size={40} />
          <p className="text-slate-500 font-medium">Loading pages...</p>
        </div>
      ) : pages.length === 0 ? (
        <div className="text-center py-24 bg-white rounded-3xl border-2 border-dashed border-slate-100 shadow-sm">
          <div className="bg-slate-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 text-slate-400">
            <FileText size={40} />
          </div>
          <h3 className="text-xl font-bold text-slate-800">
            No pages yet
          </h3>
          <p className="text-slate-500 mb-8 max-w-xs mx-auto">
            Create your first page to start managing custom content.
          </p>
          {canCreate && <Button
            onClick={() => navigate("/pages/add")}
            className="bg-primary hover:bg-primary/90 text-secondary rounded-xl"
          >
            Create Your First Page
          </Button>}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/50">
                <TableHead>Page Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Created At</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pages.map((item) => (
                <TableRow
                  key={item.id}
                  className="hover:bg-slate-50/50 transition-colors"
                >
                  <TableCell>
                    <span className="font-semibold text-slate-900">
                      {item.page_name}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2 font-mono text-sm text-slate-600">
                      {getPublicPagePath(item)}
                      <a
                        href={`https://five-online-eight.vercel.app${getPublicPagePath(item)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        <ExternalLink size={14} />
                      </a>
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getCategoryBadgeClasses(item.category)}`}>
                      {item.category}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-slate-500">
                      {new Date(item.created_at).toLocaleDateString()}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {canUpdate && <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          navigate(`/pages/edit/${item.id}`)
                        }
                        className="text-slate-500 hover:text-primary hover:bg-primary/10 h-8 w-8 p-0 rounded-full"
                      >
                        <Pencil size={16} />
                      </Button>}
                      {canDelete && <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setItemToDelete(item.id);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-slate-500 hover:text-red-500 hover:bg-red-50 h-8 w-8 p-0 rounded-full"
                      >
                        <Trash2 size={16} />
                      </Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <div className="mx-auto w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-4">
              <AlertCircle className="text-red-500" size={24} />
            </div>
            <DialogTitle className="text-center text-xl font-bold text-slate-900">Confirm Deletion</DialogTitle>
            <DialogDescription className="text-center text-slate-500 mt-2">
              Are you sure you want to delete this page? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-6 flex flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="flex-1 rounded-2xl border-slate-200"
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
              className="flex-1 rounded-2xl bg-red-500 hover:bg-red-600"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Now'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PageList;
